import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useSite } from "@/context/SiteContext";

const WA_MESSAGE = "Olá! Vim pelo site da Casa Vale e gostaria de mais informações sobre os imóveis.";

export function WhatsAppIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export function useWhatsApp() {
  const { waLink } = useSite();
  return (message) => {
    const link = waLink(message);
    if (link) {
      window.open(link, "_blank", "noopener");
    } else {
      toast.info("O WhatsApp da Casa Vale está em configuração. Em breve este canal estará disponível.");
    }
  };
}

function Header() {
  const [open, setOpen] = useState(false);
  const openWa = useWhatsApp();
  const location = useLocation();

  const navLink = ({ isActive }) =>
    `text-sm font-semibold tracking-wide transition-colors duration-200 ${
      isActive ? "text-brand" : "text-slate-600 hover:text-slate-900"
    }`;

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-md border-b border-slate-200/80" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" aria-label="Casa Vale - Início" data-testid="header-logo-link">
          <Logo />
        </Link>
        <nav className="hidden md:flex items-center gap-8" aria-label="Navegação principal">
          <NavLink to="/" end className={navLink} data-testid="nav-inicio">Início</NavLink>
          <NavLink to="/imoveis" className={navLink} data-testid="nav-imoveis">Imóveis</NavLink>
          <NavLink to="/sobre" className={navLink} data-testid="nav-sobre">Sobre Nós</NavLink>
          <button
            type="button"
            onClick={() => openWa(WA_MESSAGE)}
            className="text-sm font-semibold tracking-wide text-slate-600 hover:text-slate-900 transition-colors duration-200"
            data-testid="nav-contato-whatsapp"
          >
            Contato via WhatsApp
          </button>
        </nav>
        <div className="hidden md:block">
          <button
            type="button"
            onClick={() => openWa(WA_MESSAGE)}
            className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors duration-200"
            data-testid="header-whatsapp-btn"
          >
            <WhatsAppIcon className="w-4 h-4" />
            Fale Conosco
          </button>
        </div>
        <button
          type="button"
          className="md:hidden p-2 text-slate-800"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t border-slate-100 shadow-lg" data-testid="mobile-menu">
          <nav className="px-6 py-5 flex flex-col gap-4" aria-label="Navegação móvel">
            <NavLink to="/" end onClick={() => setOpen(false)} className="text-base font-semibold text-slate-800" data-testid="mobile-nav-inicio">Início</NavLink>
            <NavLink to="/imoveis" onClick={() => setOpen(false)} className="text-base font-semibold text-slate-800" data-testid="mobile-nav-imoveis">Imóveis</NavLink>
            <NavLink to="/sobre" onClick={() => setOpen(false)} className="text-base font-semibold text-slate-800" data-testid="mobile-nav-sobre">Sobre Nós</NavLink>
            <button
              type="button"
              onClick={() => { setOpen(false); openWa(WA_MESSAGE); }}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-brand text-white text-sm font-bold px-5 py-3 rounded-full"
              data-testid="mobile-whatsapp-btn"
            >
              <WhatsAppIcon className="w-4 h-4" />
              Contato via WhatsApp
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { settings } = useSite();
  const openWa = useWhatsApp();
  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <Logo light />
            <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-xs">
              Casas e apartamentos prontos para morar, com qualidade construtiva e atenção a cada detalhe.
            </p>
          </div>
          <div>
            <h3 className="text-white text-sm font-bold uppercase tracking-widest mb-4">Navegação</h3>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/" className="hover:text-white transition-colors" data-testid="footer-nav-inicio">Início</Link></li>
              <li><Link to="/imoveis" className="hover:text-white transition-colors" data-testid="footer-nav-imoveis">Imóveis</Link></li>
              <li><Link to="/sobre" className="hover:text-white transition-colors" data-testid="footer-nav-sobre">Sobre Nós</Link></li>
              <li><Link to="/admin" className="hover:text-white transition-colors" data-testid="footer-nav-admin">Área Administrativa</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-white text-sm font-bold uppercase tracking-widest mb-4">Contato</h3>
            <button
              type="button"
              onClick={() => openWa(WA_MESSAGE)}
              className="inline-flex items-center gap-2 text-sm hover:text-white transition-colors"
              data-testid="footer-whatsapp-link"
            >
              <WhatsAppIcon className="w-4 h-4 text-brand" />
              WhatsApp {settings.whatsapp_number ? "" : "(em configuração)"}
            </button>
            <div className="flex items-center gap-3 mt-5">
              {settings.instagram_url ? (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="p-2 rounded-full bg-slate-800 hover:bg-brand transition-colors" data-testid="footer-instagram-link">
                  <Instagram className="w-4 h-4" />
                </a>
              ) : (
                <span className="p-2 rounded-full bg-slate-800/60 text-slate-500" title="Instagram em configuração" data-testid="footer-instagram-placeholder">
                  <Instagram className="w-4 h-4" />
                </span>
              )}
              {settings.facebook_url ? (
                <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="p-2 rounded-full bg-slate-800 hover:bg-brand transition-colors" data-testid="footer-facebook-link">
                  <Facebook className="w-4 h-4" />
                </a>
              ) : (
                <span className="p-2 rounded-full bg-slate-800/60 text-slate-500" title="Facebook em configuração" data-testid="footer-facebook-placeholder">
                  <Facebook className="w-4 h-4" />
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="border-t border-slate-800 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} Casa Vale Engenharia e Construção Ltda. Todos os direitos reservados.</p>
          <p>Política de Privacidade · documento a publicar</p>
        </div>
      </div>
    </footer>
  );
}

function WhatsAppFloat() {
  const openWa = useWhatsApp();
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  return (
    <button
      type="button"
      onClick={() => openWa(WA_MESSAGE)}
      className="wa-pulse fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-brand hover:bg-brand-dark text-white flex items-center justify-center shadow-xl transition-transform duration-200 hover:scale-105"
      aria-label="Fale conosco no WhatsApp"
      title="Fale Conosco no WhatsApp"
      data-testid="whatsapp-floating-btn"
    >
      <WhatsAppIcon className="w-7 h-7" />
    </button>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
